/**
 * Iterative-deepening negamax with alpha-beta, over the shared interpreter.
 *
 * The interpreter is the single source of rules, so the search is correct for
 * any spec by construction: it never asks what game it is playing, only what
 * moves are legal and whether the position is terminal.
 *
 * Three decisions worth keeping:
 *
 *  - **A budget, not a fixed depth.** A late position with eight empty cells is
 *    solved exactly; the opening degrades to whatever depth fits. A fixed depth
 *    would be either uselessly shallow at the end or unusably slow at the start.
 *    The budget comes in two currencies, and which one a caller picks is a
 *    correctness decision rather than a preference — see `SearchOptions`.
 *  - **Mate-distance scores.** A win is `WIN_SCORE - ply`, so "wins in 3" beats
 *    "wins in 9" and the engine actually finishes games instead of shuffling.
 *  - **Every root move gets an exact-window search.** Review has to say how bad
 *    the move you *played* was, not just name the best one, so the root cannot
 *    narrow the window between siblings the way a playing engine would.
 *
 * Purity: no I/O and no randomness. The clock is injectable, and under a node
 * budget it is not consulted at all, so the result is a pure function of the
 * position — the same call on a loaded CI box and an idle laptop returns the
 * same thing.
 */
import { boardSize, legalMoves } from '../spec/interpreter.ts';
import type { State } from '../spec/interpreter.ts';
import { MAX_BOARD_CELLS } from '../spec/schema.ts';
import type { RulesSpec } from '../spec/schema.ts';
import { positionFromState } from './position.ts';
import type { MutablePosition } from './position.ts';

/**
 * A forced win at ply 0. Comfortably above `HEURISTIC_MAX`, so the mate band and
 * the heuristic band can never overlap however large a board a spec declares.
 */
export const WIN_SCORE = 1_000_000;

/**
 * Scores at least this large are proven, not estimated.
 *
 * A game cannot last more plies than the board has cells, so no real mate
 * distance can reach below this line — and `HEURISTIC_MAX` is an order of
 * magnitude further down still, which is what keeps the two bands disjoint.
 */
export const MATE_THRESHOLD = WIN_SCORE - MAX_BOARD_CELLS;

const INFINITY_SCORE = WIN_SCORE + 1;

/** How often the clock is consulted. Reading it per node costs more than the search. */
const CLOCK_INTERVAL_NODES = 2048;

/** What a score of this size means for the side that owns it. */
export type Outcome = 'win' | 'loss' | 'draw' | 'unknown';

export interface MoveScore {
  /** The move's parameter — a column, in the current vocabulary. */
  readonly param: number;
  /** Score from the point of view of the seat to move. Higher is better. */
  readonly score: number;
}

export interface SearchResult {
  /** Every legal move, best first, then by ascending parameter for stability. */
  readonly moves: readonly MoveScore[];
  /** The deepest iteration that completed. 0 when the position is terminal. */
  readonly depth: number;
  /** Positions visited, across every iteration. */
  readonly nodes: number;
  /**
   * True when the search stopped because it ran out of depth or proved the
   * position, rather than because it ran out of time.
   */
  readonly completed: boolean;
}

export interface SearchOptions {
  /**
   * Wall-clock milliseconds for this position. Defaults to 200 when no node
   * budget is given either.
   *
   * The right currency when something is *waiting*: a bot owing a move to a live
   * match, or a review the player is watching render. It buys responsiveness at
   * the cost of reproducibility — a slower machine searches less deeply and gets
   * a different answer.
   */
  readonly budgetMs?: number;
  /**
   * Positions to visit before giving up. Deterministic: the same call returns
   * the same result on any machine, under any load.
   *
   * The right currency when a *result* is being computed rather than served —
   * hunting drills and puzzles, and anything a test asserts on. Passing this
   * without `budgetMs` switches the clock off entirely.
   */
  readonly maxNodes?: number;
  /**
   * Deepest iteration to attempt. Defaults to the number of empty cells, which
   * is the depth at which the position is solved exactly.
   */
  readonly maxDepth?: number;
  /** Injectable clock. Tests use it to prove the time budget is honoured. */
  readonly now?: () => number;
}

/** Time allowed when a caller names neither currency. */
const DEFAULT_BUDGET_MS = 200;

/** True when `score` is a proven result rather than a heuristic estimate. */
export function isDecisive(score: number): boolean {
  return score >= MATE_THRESHOLD || score <= -MATE_THRESHOLD;
}

/** The outcome a score asserts, or `'unknown'` when it is only an estimate. */
export function outcomeOf(score: number, exact: boolean): Outcome {
  if (score >= MATE_THRESHOLD) return 'win';
  if (score <= -MATE_THRESHOLD) return 'loss';
  return exact && score === 0 ? 'draw' : 'unknown';
}

/**
 * Plies until the win a decisive score promises, or null when the score is not
 * a proven win. A loss returns the distance too — it is still a mate, just the
 * other way round.
 */
export function mateDistance(score: number): number | null {
  if (score >= MATE_THRESHOLD) return WIN_SCORE - score;
  if (score <= -MATE_THRESHOLD) return WIN_SCORE + score;
  return null;
}

/**
 * Move parameters ordered centre-first.
 *
 * Derived from `board.cols`, not from Connect 4 lore: in any game where lines
 * run through a grid, a central column sits inside more windows than an edge
 * one, so it is more likely to be best and therefore more likely to cause a
 * cutoff. Ties break towards the lower parameter, which keeps the search
 * deterministic.
 */
export function centerFirstOrder(cols: number): readonly number[] {
  const middle = (cols - 1) / 2;
  return Object.freeze(
    Array.from({ length: cols }, (_unused, param) => param).sort((a, b) => {
      const distance = Math.abs(a - middle) - Math.abs(b - middle);
      return distance !== 0 ? distance : a - b;
    }),
  );
}

/** Transposition table entry. `flag` records what the score is relative to its window. */
interface Entry {
  readonly depth: number;
  readonly score: number;
  readonly flag: 'exact' | 'lower' | 'upper';
  readonly bestParam: number;
}

/** Thrown internally when the budget runs out; never escapes `searchPosition`. */
class BudgetExhausted extends Error {}

/**
 * Negate a score, normalising `-0` to `0`.
 *
 * A drawn line otherwise alternates between `0` and `-0` up the tree, which
 * compares equal in arithmetic, survives a structural equality check, and then
 * changes to `0` the moment the result crosses `JSON.stringify` into a worker
 * message. Better to never produce it.
 */
function negate(score: number): number {
  return -score + 0;
}

/**
 * Score every legal move in `state`, best first.
 *
 * Terminal positions return no moves and depth 0 — the caller is expected to ask
 * the interpreter what happened, because the search has no opinion about a game
 * that is already over.
 */
export function searchPosition<S extends RulesSpec>(
  rules: S,
  state: State<S>,
  options: SearchOptions = {},
): SearchResult {
  // The interpreter decides whether there is anything to search. The mutable
  // position below is a cache of its rules, never a second opinion about them.
  const rootMoves = legalMoves(rules, state);
  if (rootMoves.length === 0) {
    return { moves: Object.freeze([]), depth: 0, nodes: 0, completed: true };
  }

  const now = options.now ?? (() => Date.now());
  const maxNodes = options.maxNodes ?? Number.POSITIVE_INFINITY;
  /*
   * A node budget on its own means the clock is never read. That is the whole
   * point of it: a search that consults `Date.now()` cannot be reproduced, and
   * a caller that asked for determinism should not silently also get a deadline
   * it never named.
   */
  const deadline =
    options.budgetMs !== undefined
      ? now() + options.budgetMs
      : options.maxNodes !== undefined
        ? Number.POSITIVE_INFINITY
        : now() + DEFAULT_BUDGET_MS;

  let emptyCells = 0;
  const size = boardSize(rules);
  for (let index = 0; index < size; index += 1) {
    if (state.board[index] === rules.cells.empty) emptyCells += 1;
  }
  const maxDepth = Math.max(1, Math.min(options.maxDepth ?? emptyCells, emptyCells));

  const order = centerFirstOrder(rules.board.cols);
  const context: SearchContext = {
    position: positionFromState(rules, state),
    table: new Map<number, Entry>(),
    order,
    now,
    deadline,
    timed: Number.isFinite(deadline),
    maxNodes,
    nodes: 0,
    sinceClockCheck: 0,
  };

  const orderedRoot = order.filter((param) =>
    rootMoves.some((move) => move.param === param),
  );

  let best: MoveScore[] = orderedRoot.map((param) => ({ param, score: 0 }));
  let completedDepth = 0;
  let completed = false;

  for (let depth = 1; depth <= maxDepth; depth += 1) {
    // Search the previous iteration's best first: it is the likeliest cutoff,
    // and it is what makes iterative deepening cheaper than its deepest pass.
    const ordered = [...orderedRoot].sort(
      (a, b) => rankOf(best, a) - rankOf(best, b) || order.indexOf(a) - order.indexOf(b),
    );

    const scored: MoveScore[] = [];
    let aborted = false;
    for (const param of ordered) {
      if (context.position.play(param) === -1) continue;
      try {
        // A full window per root move: review has to report how bad the move
        // that was *played* is, and a narrowed window would only bound it.
        const score = negate(negamax(context, depth - 1, -INFINITY_SCORE, INFINITY_SCORE, 1));
        scored.push({ param, score });
      } catch (error) {
        if (!(error instanceof BudgetExhausted)) throw error;
        aborted = true;
        break;
      } finally {
        context.position.undo();
      }
    }

    if (aborted) break;

    // A complete iteration replaces the previous one wholesale. A partial one is
    // discarded: half its moves would be scored a ply deeper than the rest,
    // which is not a ranking, it is a mixture.
    best = sortMoves(scored, order);
    completedDepth = depth;

    // Every line is proven, so a deeper look cannot change any score.
    if (best.every((entry) => isDecisive(entry.score))) {
      completed = true;
      break;
    }
    if (depth === maxDepth) completed = true;
  }

  return {
    moves: Object.freeze(best),
    depth: completedDepth,
    nodes: context.nodes,
    completed,
  };
}

interface SearchContext {
  readonly position: MutablePosition;
  readonly table: Map<number, Entry>;
  readonly order: readonly number[];
  readonly now: () => number;
  readonly deadline: number;
  /** False under a pure node budget, when the clock is never read at all. */
  readonly timed: boolean;
  readonly maxNodes: number;
  nodes: number;
  sinceClockCheck: number;
}

function rankOf(moves: readonly MoveScore[], param: number): number {
  const index = moves.findIndex((entry) => entry.param === param);
  return index === -1 ? moves.length : index;
}

function sortMoves(moves: readonly MoveScore[], order: readonly number[]): MoveScore[] {
  return [...moves].sort(
    (a, b) => b.score - a.score || order.indexOf(a.param) - order.indexOf(b.param),
  );
}

/**
 * Negamax with alpha-beta over the mutable position.
 *
 * `ply` is the distance from the root, and it is what makes mate scores
 * comparable across the tree: a win found deeper is worth less than the same
 * win found shallower, so the engine prefers the quickest kill and the fastest
 * defence.
 *
 * Nothing in here allocates. The move loop walks the precomputed centre-first
 * order in place, with the transposition table's suggestion tried first.
 */
function negamax(
  context: SearchContext,
  depth: number,
  alpha: number,
  beta: number,
  ply: number,
): number {
  context.nodes += 1;
  // Checked every node, not every 2048: a node budget is only deterministic if
  // it bites at the same node every time. One integer compare is nothing beside
  // the move loop below, whereas reading the clock is not.
  if (context.nodes > context.maxNodes) throw new BudgetExhausted();

  if (context.timed) {
    context.sinceClockCheck += 1;
    if (context.sinceClockCheck >= CLOCK_INTERVAL_NODES) {
      context.sinceClockCheck = 0;
      if (context.now() >= context.deadline) throw new BudgetExhausted();
    }
  }

  const position = context.position;

  // A win is always recorded by the *previous* mover, so from here it is a loss.
  if (position.lastMoveWon()) return -(WIN_SCORE - ply);
  if (position.isDrawn()) return 0;
  if (depth === 0) return position.evaluateFor(position.toMove);

  const key = position.key();
  const cached = context.table.get(key);
  let hintParam = -1;
  if (cached !== undefined) {
    if (cached.depth >= depth) {
      if (cached.flag === 'exact') return cached.score;
      if (cached.flag === 'lower' && cached.score > alpha) alpha = cached.score;
      if (cached.flag === 'upper' && cached.score < beta) beta = cached.score;
      if (alpha >= beta) return cached.score;
    }
    hintParam = cached.bestParam;
  }

  const order = context.order;
  const originalAlpha = alpha;
  let bestScore = -INFINITY_SCORE;
  let bestParam = -1;
  let played = 0;

  // `index === -1` is the table's suggestion; the rest is centre-first order.
  for (let index = -1; index < order.length; index += 1) {
    const param = index === -1 ? hintParam : (order[index] ?? -1);
    if (param < 0) continue;
    if (index >= 0 && param === hintParam) continue;
    if (position.play(param) === -1) continue;
    played += 1;
    const score = negate(negamax(context, depth - 1, -beta, -alpha, ply + 1));
    position.undo();
    if (score > bestScore) {
      bestScore = score;
      bestParam = param;
    }
    if (score > alpha) alpha = score;
    if (alpha >= beta) break;
  }

  // No legal move and no terminal condition: a spec that declares no draw can
  // fill its board and simply stop. Score it as it stands.
  if (played === 0) return position.evaluateFor(position.toMove);

  const flag: Entry['flag'] =
    bestScore <= originalAlpha ? 'upper' : bestScore >= beta ? 'lower' : 'exact';
  const previous = context.table.get(key);
  if (previous === undefined || previous.depth <= depth) {
    context.table.set(key, { depth, score: bestScore, flag, bestParam });
  }

  return bestScore;
}

/** Convenience for callers that only want the engine's choice. */
export function bestMove<S extends RulesSpec>(
  rules: S,
  state: State<S>,
  options: SearchOptions = {},
): MoveScore | null {
  const result = searchPosition(rules, state, options);
  return result.moves[0] ?? null;
}
